import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import {
    loadScreenings,
    tktsyncLegacyScreenings,
    importOzuZipLegacyScreenings,
    clearLastScreeningImportLogs,
    OZU_IMPORT_FILE_TOO_LARGE
} from '../../redux';
import moment from 'moment';
import { Log, Setting } from '../../models';
import { Utils, reapplyPricingsOnScreeningsUpdate, willSyncScreeningPlaces } from '../../services';
import ImportInvalidMessage from '../ImportInvalidMessage/ImportInvalidMessage';
import InvalidImportEntryTitle from '../InvalidImportEntryTitle/InvalidImportEntryTitle';
import SyncFileImportSection from '../SynchronisationPanel/SyncFileImportSection/SyncFileImportSection';
import classnames from 'classnames';

import { plainApiErrorMessage } from './plainApiErrorMessage';
import './ScreeningsImporter.css';

const OZU_ZIP_MAX_BYTES = 300 * 1024 * 1024;

const ScreeningsImporter = ({ visible }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState('screenings');
    const [ozuZipFile, setOzuZipFile] = useState(null);
    const [ozuUploadError, setOzuUploadError] = useState(null);

    const {
        isTktsyncing,
        hasTktsyncingError,
        tktsyncingErrorMessage
    } = useSelector(state => state.legacyScreenings);

    const { setting }                            = useSelector(state => state.settings);
    const { lastScreeningImportLogs, isLoading } = useSelector(state => state.logs);

    const config = React.useMemo(() => setting ? (new Setting(setting)).getScreeningsImportConfiguration() : null, [setting]);

    const toggle = tab => {
        if (activeTab !== tab)
            setActiveTab(tab);
    }


    const onImportSuccess = err => {
        if (!err)
            dispatch(loadScreenings({
                start_at_gte: moment().startOf('day').toISOString(),
            }));
    };

    const tktsync = _e => {
        dispatch(clearLastScreeningImportLogs());
        dispatch(tktsyncLegacyScreenings(onImportSuccess));
    };

    const onOzuZipSelected = file => {
        setOzuUploadError(null);

        if (!file)
            return;

        if (!file.name?.toLowerCase().endsWith('.zip')) {
            setOzuZipFile(null);
            setOzuUploadError('screenings.import.ozu_unsupported_file_type');
            return;
        }

        if (file.size > OZU_ZIP_MAX_BYTES) {
            setOzuZipFile(null);
            setOzuUploadError('screenings.import.ozu_file_too_large');
            return;
        }

        setOzuZipFile(file);
    };

    const importOzuZip = () => {
        if (!ozuZipFile)
            return;

        dispatch(clearLastScreeningImportLogs());
        dispatch(importOzuZipLegacyScreenings(ozuZipFile, (err) => {
            if (!err)
                setOzuZipFile(null);
            onImportSuccess(err);
        }));
    };

    const isUseless = log => {
        if (!log )
            return false;

        return (
            log.inserted?.length === 0 &&
            log.updated?.length === 0 &&
            log.invalid?.length === 0
        );
    };

    const willSyncPlaces = React.useMemo(
        () => willSyncScreeningPlaces(config),
        [config]
    );

    const screeningsImportResume = React.useMemo(() => log => (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    { t('screenings.import.resume') }
                </h5>
                <ul>
                    <li>
                        <b>{ t('screenings.import.nb_deleted', { count: log.nbDeleted }) }</b>
                    </li>
                    <li>
                        <b>{ t('screenings.import.nb_inserted', { count: log.inserted?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('screenings.import.nb_updated', { count: log.updated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('screenings.import.nb_invalid', { count: log.invalid?.length }) }</b>
                    </li>
                </ul>
            </div>
            {isUseless(log) && (
                <div className="bg-info mt-2 p-2 rounded">
                    <b>
                        { t('screenings.import.was_useless') }
                    </b>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('screenings.import.inserted', { count: log.inserted.length}) }
                    </h5>
                    <ul>
                    {log.inserted.map(screening => (
                        <li key={screening._id} className="screening">
                            <Link to={`/screenings/edit/${screening._id}`}>
                                <b>
                                    {Utils.localized_or_fallback(screening.title, i18n.language)}
                                </b>
                            </Link>
                            - { Utils.formatLocalizedDateTime(screening.start_at, i18n.language, 'LLL') }
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('screenings.import.updated', { count: log.updated.length}) }
                    </h5>
                    <ul>
                    {log.updated.map(screening => (
                        <li key={screening._id} className="screening">
                            <Link to={`/screenings/edit/${screening._id}`}>
                                <b>
                                    {Utils.localized_or_fallback(screening.title, i18n.language)}
                                </b>
                            </Link>
                            - { Utils.formatLocalizedDateTime(screening.start_at, i18n.language, 'LLL') }
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        { t('screenings.import.invalid', { count: log.invalid.length}) }
                    </h5>
                    {log.invalid.map((entry, i) => (
                        <div key={i} className="screening">
                            <InvalidImportEntryTitle
                                importSource={log.source}
                                setting={setting}
                                resource="screenings"
                                entry={entry}
                                title={Utils.localized_or_fallback(entry.title, i18n.language)}
                                subtitle={Utils.formatLocalizedDateTime(entry.start_at, i18n.language, 'LLL')}
                                editPath={entry._id ? `/screenings/edit/${entry._id}` : null}
                            />
                            <ul>
                                {Object.keys(entry.invalid).map(key => (
                                    <ImportInvalidMessage
                                        key={key}
                                        resource="screenings"
                                        fieldKey={key}
                                        code={entry.invalid[key]}
                                        importSource={log.source}
                                        entry={entry}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ), [i18n.language, setting, t]);

    const placesImportResume = React.useMemo(() => log => (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    { t('places.import.resume') }
                </h5>
                <ul>
                    <li>
                        <b>{ t('places.import.nb_deleted', { count: log.nbDeleted }) }</b>
                    </li>
                    <li>
                        <b>{ t('places.import.nb_inserted', { count: log.inserted?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('places.import.nb_updated', { count: log.updated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('places.import.nb_invalid', { count: log.invalid?.length }) }</b>
                    </li>
                </ul>
            </div>
            {isUseless(log) && (
                <div className="bg-info mt-2 p-2 rounded">
                    <b>
                        { t('places.import.was_useless') }
                    </b>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('places.import.inserted') }
                    </h5>
                    <ul>
                    {log.inserted.map(place => (
                        <li key={place._id} className="place">
                            <Link to={`/places/edit/${place._id}`}>
                                <b> { place.name } </b>
                            </Link>
                            - { place.inema }
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('places.import.updated') }
                    </h5>
                    <ul>
                    {log.updated.map(place => (
                        <li key={place._id} className="place">
                            <Link to={`/places/edit/${place._id}`}>
                                <b> { place.name } </b>
                            </Link>
                            - { place.inema }
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        { t('places.import.invalid') }
                    </h5>
                    {log.invalid.map((entry, i) => (
                        <div key={i} className="place">
                            <InvalidImportEntryTitle
                                importSource={log.source}
                                setting={setting}
                                resource="places"
                                entry={entry}
                                title={entry.name}
                                subtitle={entry.cinema || entry.inema}
                                editPath={entry._id ? `/places/edit/${entry._id}` : null}
                            />
                            <ul>
                                {Object.keys(entry.invalid).map(key => (
                                    <ImportInvalidMessage
                                        key={key}
                                        resource="places"
                                        fieldKey={key}
                                        code={entry.invalid[key]}
                                        importSource={log.source}
                                        entry={entry}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ), [setting, t]);

    const color = hasTktsyncingError ? 'warning' : 'info';

    const placesImportLogs     = lastScreeningImportLogs?.filter(log => log.type === Log.PLACES_IMPORT);
    const screeningsImportLogs = lastScreeningImportLogs?.filter(log => log.type === Log.SCREENINGS_IMPORT);

    if (!visible || !config)
        return null;

    const deletePolicy = config.import.screenings.delete_future
        ? t('events.tktsync_delete_future')
        : config.import.screenings.delete_existing
            ? t('events.tktsync_delete_existing')
            : t('events.tktsync_no_delete');

    // Engine update rules (importer/screenings): bookings always kept; buckets
    // and cinema_hall never overwritten on update; pricings only when the source
    // specifies a list (ozu/cinech never reapply on update). Missing lists/venue
    // on create use the Kronos default.
    const reapplyPricingsOnUpdate = reapplyPricingsOnScreeningsUpdate(config.source);
    const updatePolicyItems = [
        deletePolicy,
        t('events.tktsync_keep_bookings'),
        t('events.tktsync_update_metadata'),
        reapplyPricingsOnUpdate
            ? t('events.tktsync_reapply_pricings')
            : t('events.tktsync_keep_pricings'),
        t('events.tktsync_keep_buckets'),
        t('events.tktsync_keep_venues'),
        t('events.tktsync_defaults_on_create'),
    ];

    const screeningsHelp = (
        <>
            <p>{ t(`events.tktsync_${config?.source}_desc_screenings`) }</p>
            {willSyncPlaces && (
                <p>{ t(`events.tktsync_${config?.source}_desc_places`) }</p>
            )}
            <ul className="SyncFileImportSection-policyList">
                {updatePolicyItems.map(item => (
                    <li key={ item } className="SyncFileImportSection-policy">
                        <i className="fa fa-info-circle" aria-hidden="true" />
                        <span>{ item }</span>
                    </li>
                ))}
            </ul>
            {config.source === 'ozu' ? (
                <p>{ t('screenings.import.ozu_zip_contents') }</p>
            ) : null}
        </>
    );

    const sourceLabel = t(`screenings.import.sources.${config.source}`, {
        defaultValue: config.source,
    });
    const importTitle = t('screenings.import.from_source', { source: sourceLabel });

    return (
        <div className="ScreeningsImporter">
            {config.source === 'ozu' ? (
                <SyncFileImportSection
                    title={ importTitle }
                    help={ screeningsHelp }
                    formats={ t('screenings.import.ozu_formats') }
                    accept=".zip,application/zip"
                    disabled={ isTktsyncing }
                    fileName={ ozuZipFile?.name }
                    onFile={ onOzuZipSelected }
                    downloadSlot={ setting?.integrations?.ozu?.url?.length > 0 ? (
                        <Button onClick={ tktsync } color="info" size="sm" disabled={ isTktsyncing }>
                            <i className={`fa ${isTktsyncing ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                            <span>&nbsp;{ t('screenings.import.ozu_sync_from_url') }</span>
                        </Button>
                    ) : null }
                >
                    {ozuUploadError && (
                        <div className="mt-2 text-danger">
                            { t(ozuUploadError) }
                        </div>
                    )}
                    <Button
                        onClick={ importOzuZip }
                        color={ color }
                        className="mt-3"
                        disabled={ isTktsyncing || !ozuZipFile }
                    >
                        <i className={`fa ${isTktsyncing ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                        <span>&nbsp;{ t('screenings.import.ozu_do_import') }</span>
                    </Button>
                </SyncFileImportSection>
            ) : (
                <SyncFileImportSection
                    title={ importTitle }
                    help={ screeningsHelp }
                    showDropzone={ false }
                    downloadSlot={
                        <Button onClick={ tktsync } color="info" size="sm" disabled={ isTktsyncing }>
                            <i className={`fa ${isTktsyncing ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                            <span>&nbsp;{ t("events.run_tktsync") }</span>
                        </Button>
                    }
                />
            )}

            <hr />

            <div className="d-flex mt-2">
                {(isLoading || isTktsyncing) ? (
                    <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                ) : (
                    <div className="mt-2 w-100">
                        {hasTktsyncingError && (
                            <div className="mt-2 bg-danger p-2 rounded ScreeningsImporter-error">
                                { tktsyncingErrorMessage === OZU_IMPORT_FILE_TOO_LARGE
                                    ? t('screenings.import.ozu_file_too_large')
                                    : (plainApiErrorMessage(tktsyncingErrorMessage) || t('screenings.import.an_error_occured')) }
                            </div>
                        )}

                        {(lastScreeningImportLogs?.length > 0) && (
                        <>
                            <h4 className="text-center text-underline">
                                { t('screenings.import.imported_at', { date: Utils.formatLocalizedDateTime(lastScreeningImportLogs[0].created_at, i18n.language, 'LLL') }) }
                            </h4>
                            <br />
                            <Nav tabs>
                                {(screeningsImportLogs?.length > 0) && (
                                    <NavItem>
                                        <NavLink className={classnames({ active: activeTab === 'screenings' })} onClick={() => { toggle('screenings'); }}>
                                            {t('common.screening', {count: 42})}
                                            {(screeningsImportLogs[0].invalid.length > 0) && (
                                                <i className="fa fa-warning ml-2"></i>
                                            )}
                                        </NavLink>
                                    </NavItem>
                                )}
                                {(placesImportLogs?.length > 0) && (
                                    <NavItem>
                                        <NavLink className={classnames({ active: activeTab === 'places' })} onClick={() => { toggle('places'); }}>
                                            {t('places.import.places_tab')}
                                            {(placesImportLogs[0].invalid.length > 0) && (
                                                <i className="fa fa-warning ml-2"></i>
                                            )}
                                        </NavLink>
                                    </NavItem>
                                )}
                            </Nav>
                            <br />
                            <TabContent activeTab={activeTab}>
                                {(screeningsImportLogs?.length > 0) && (
                                    <TabPane tabId="screenings">
                                        { screeningsImportLogs?.map(log => screeningsImportResume(log)) }
                                    </TabPane>
                                )}
                                {(placesImportLogs?.length > 0) && (
                                    <TabPane tabId="places">
                                        { placesImportLogs?.map(log => placesImportResume(log)) }
                                    </TabPane>
                                )}
                            </TabContent>
                        </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ScreeningsImporter;
